import type {
  SimOverride,
  SimParam,
  SimParamType,
  SimProfileDraft,
} from "@/types/simulations.types";

// Os 5 params tipados do sandbox. O builder antigo foi
// aposentado; esta é a única cópia viva. Espelha o FIELD_META do worker
// (apps/worker/src/consumers/simulations.engine-params.ts).
export const PARAM_TYPES: readonly SimParamType[] = [
  {
    id: "feedRate",
    label: "Ritmo de alimentação",
    unit: "un/h",
    betterDirection: "up",
    field: "feedRateUnH",
  },
  {
    id: "cycleTime",
    label: "Tempo de ciclo",
    unit: "s",
    betterDirection: "down",
    field: "cycleTimeS",
  },
  { id: "mtbf", label: "MTBF", unit: "min", betterDirection: "up", field: "mtbfMin" },
  { id: "mttr", label: "MTTR", unit: "min", betterDirection: "down", field: "mttrMin" },
  {
    id: "quality",
    label: "Meta de qualidade",
    unit: "%",
    betterDirection: "up",
    field: "qualityTargetPct",
  },
] as const;

export const OVERRIDE_LIMIT_PCT = 25;

export function paramType(id: SimParam): SimParamType {
  const t = PARAM_TYPES.find((p) => p.id === id);
  if (!t) throw new Error(`Param desconhecido: ${id}`);
  return t;
}

/** Clamp da variação de um override a ±25%. */
export function clampDelta(deltaPct: number): number {
  return Math.min(OVERRIDE_LIMIT_PCT, Math.max(-OVERRIDE_LIMIT_PCT, deltaPct));
}

/** Adiciona/atualiza um override por par (param); o último vence, delta clampado a ±25%. */
export function upsertOverride(
  overrides: SimOverride[],
  param: SimParam,
  deltaPct: number
): SimOverride[] {
  const next = overrides.filter((o) => o.param !== param);
  next.push({ param, deltaPct: clampDelta(deltaPct) });
  return next;
}

export function removeOverride(overrides: SimOverride[], param: SimParam): SimOverride[] {
  return overrides.filter((o) => o.param !== param);
}

/** Valor base do param a partir do rascunho do perfil (undefined se não informado). */
export function baseValue(profile: SimProfileDraft, param: SimParam): number | undefined {
  return profile[paramType(param).field];
}

/** Valor resultante após aplicar o override (preview "de → para"); undefined sem base. */
export function appliedValue(
  profile: SimProfileDraft,
  param: SimParam,
  deltaPct: number
): number | undefined {
  const base = baseValue(profile, param);
  if (base === undefined) return undefined;
  return base * (1 + deltaPct / 100);
}
